import React from 'react';

import { createPortal } from 'react-dom';

import { useClassnames } from '@lumapps/classnames';
import { DndContext, SortableContext, DragOverlay } from '@lumapps/lumx-dnd/components';
import { useSensors, useSensor } from '@lumapps/lumx-dnd/hooks';
import type { UniqueIdentifier, DragEndEvent } from '@lumapps/lumx-dnd/types';
import {
    PointerSensor,
    TouchSensor,
    arrayMove,
    MeasuringStrategy,
    closestCenter,
    rectSortingStrategy,
    defaultDropAnimationSideEffects,
} from '@lumapps/lumx-dnd/utils';
import { ItemRef } from '@lumapps/lumx-lists/components/SortableList/SortableListItem';
import { type Path, ReactEditor, useSlateStatic } from '@lumapps/wrex/slate';
import { EditableWrapper, EditableComponent, ElementRenderer } from '@lumapps/wrex/slate/render.types';

import { DragAndDropChildren } from '../DragAndDropChildren';
import { DragAndDropPreview } from '../DragAndDropPreview';
import './index.scss';

const CLASSNAME = 'wrex-dnd';

export interface WithDragAndDropOptions {
    /** scope for data-ids */
    scope?: string;
    /** classname to apply to the preview wrapper */
    previewClassNames?: string[];
    /** the color of the RTE's background */
    backgroundColor?: string /* hex only */;
}

/**
 * HOC wrapper around the slate editable.
 * Handles drag and drop
 */
export const DragAndDropHOC =
    (options?: WithDragAndDropOptions): EditableWrapper =>
    (Editable) => {
        const Component: EditableComponent = React.forwardRef((props, editableRef) => {
            const { block } = useClassnames(CLASSNAME);
            const editorStatic = useSlateStatic() as ReactEditor;

            const [items, setItems] = React.useState<UniqueIdentifier[]>(() =>
                editorStatic.children.map((_, index) => index),
            );
            const [active, setActive] = React.useState<{ id: UniqueIdentifier; index: number } | null>(null);
            const itemRefs = React.useRef<ItemRef[]>([]);

            const getEntityIndex = React.useCallback((id: UniqueIdentifier) => items.indexOf(id), [items]);

            const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));

            const handleDragStart = React.useCallback(
                (event: { active: { id: UniqueIdentifier } }) => {
                    const { active } = event;
                    if (active) {
                        setActive({ id: active.id, index: getEntityIndex(active.id) });
                    }
                },
                [getEntityIndex],
            );

            const handleDragEnd = React.useCallback(
                (event: DragEndEvent) => {
                    const { active, over } = event;

                    if (over && active.id !== over.id) {
                        const oldIndex = getEntityIndex(active.id);
                        const newIndex = getEntityIndex(over.id);
                        setItems((items) => arrayMove(items, oldIndex, newIndex));
                        editorStatic.moveNodes({ at: [oldIndex], to: [newIndex] });
                    }
                    setActive(null);
                },
                [editorStatic, getEntityIndex],
            );

            const handleDragCancel = React.useCallback(() => {
                setActive(null);
            }, []);

            const measuring = React.useMemo(
                () => ({
                    droppable: {
                        strategy: MeasuringStrategy.Always,
                        frequency: 5,
                    },
                }),
                [],
            );

            const renderElement: ElementRenderer = React.useCallback(
                ({ element }, rendered: React.JSX.Element) => {
                    const path = ReactEditor.findPath(editorStatic, element) as Path;

                    // if elements are nested, only the top one should have dnd feature
                    if (path.length > 1) {
                        return rendered;
                    }

                    const index = path[0];
                    const id = items[index];

                    if (id === undefined) {
                        return rendered;
                    }

                    return (
                        <DragAndDropChildren
                            type={element?.type || ''}
                            renderedChildren={rendered}
                            id={id}
                            index={index}
                            itemRef={(el) => {
                                itemRefs.current[index] = el;
                            }}
                            scope={options?.scope}
                            isDragged={active?.id === id}
                            backgroundColor={options?.backgroundColor}
                        />
                    );
                },
                [active?.id, editorStatic, items],
            );

            // On new elements added/removed, update the items list
            React.useEffect(() => {
                itemRefs.current = [];
                setItems(editorStatic.children.map((_, index) => index));
            }, [editorStatic.children]);

            return (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                    measuring={measuring}
                >
                    <SortableContext items={items} strategy={rectSortingStrategy}>
                        <div
                            className={block()}
                            style={{ '--background-color': options?.backgroundColor } as React.CSSProperties}
                        >
                            <Editable {...props} ref={editableRef} renderElement={renderElement} />
                        </div>
                    </SortableContext>
                    {createPortal(
                        <DragOverlay
                            adjustScale={false}
                            dropAnimation={{
                                sideEffects: defaultDropAnimationSideEffects({
                                    styles: {
                                        active: {
                                            opacity: '0.5',
                                        },
                                    },
                                }),
                            }}
                        >
                            {active != null ? (
                                <DragAndDropPreview
                                    domElementToReplicate={itemRefs.current[active.index]}
                                    index={active.index}
                                    id={active.id}
                                    wrapperClassNames={options?.previewClassNames}
                                    backgroundColor={options?.backgroundColor}
                                />
                            ) : null}
                        </DragOverlay>,
                        document.body,
                    )}
                </DndContext>
            );
        });

        return Component;
    };
